import { MKLog } from "./MKLog";
import { MKSubtitleCueEvent } from "../../interfaces";

/**
 * Subtitle overlay class.
 * 
 * This class is responsible for rendering/displaying the subtitles/captions.
 * 
 * The player engine must provide {@link MKSubtitleCueEvent} data to be able to render subtitle/captions.
 * 
 * Also a valid player or video container element with a valid video element is required for this component to work correctly. The videoConainer must be the same as used at the time of MKPlayer creation and the videoElement must be the current video element that the player is using to render video.
 * 
 * @internal
 */
export class MKSubtitleOverlay {
    /**
     * The number of rows in a CEA608 grid.
     */
    private static readonly CEA608_NUM_ROWS = 15;

    /**
     * The number of columns in a CEA608 grid.
     */
    private static readonly CEA608_NUM_COLUMNS = 32;

    /**
     * Default region styling.
     */
    private static readonly DEFAULT_REGION_STYLE = "position:absolute;top:5%;left:5%;width:90%;height:90%;margin:0px;padding:0px;text-align:center;display:flex;overflow:hidden;visibility:visible;opacity:1;flex-direction:column;justify-content:flex-end;writing-mode:horizantal-tb;z-index:auto";

    /**
     * Default cell resolution.
     */
    private static readonly DEFAULT_CELL_RESOLUTION = {
        rows: 15,
        columns: 40
    };

    /**
     * Parent element of the video element and subtitle container.
     */
    private videoContainer: HTMLElement;

    /**
     * The HTML video element.
     */
    private videoElement: HTMLVideoElement;

    /**
     * The subtitle container.
     */
    private subtitleContainer: HTMLElement;

    /**
     * The subtitle region.
     */
    private subtitleRegion: HTMLElement;

    /**
     * Subtitle overlay enabled?
     */
    private enabled: boolean;

    /**
     * Subtitle style options
     */
    private subtitleStyle: {
        fontSize?: string;
        fontStyle?: string;
        fontFamily?: string;
        fontColor?: string;
        fontOpacity?: number;
        backgroundColor?: string;
    } = {};

    /**
     * Create a new instance of subtitle overlay.
     * 
     * Note: the passed video element if it does not contain a parent element an error will be thrown. Also the parent element should be the same video container used at the time of MKPlayer creation.
     * 
     * @param videoElement the HTML video element.
     */
    constructor(videoElement: HTMLVideoElement) {
        if (!videoElement.parentElement) {
            throw new Error("Parent element for passed video element not found!");
        }

        // initialize
        this.enabled = false;
        this.videoElement = videoElement;
        this.videoContainer = videoElement.parentElement;
        this.subtitleRegion = this.getSubtitleRegion();
        this.subtitleContainer = this.getSubtitleContainer();
        this.subtitleContainer.appendChild(this.subtitleRegion);
        this.videoElement.parentNode?.insertBefore(this.subtitleContainer, this.videoElement.nextSibling);
        MKLog.log("[SubtitleOverlay] created.");
    }

    private reset() {
        if (this.videoContainer && this.videoElement) {
            this.subtitleRegion = this.getSubtitleRegion();
            this.subtitleContainer = this.getSubtitleContainer();
            this.subtitleContainer.appendChild(this.subtitleRegion);
            this.videoElement.parentNode?.insertBefore(this.subtitleContainer, this.videoElement.nextSibling);
        }
    }

    private getSubtitleContainer(): HTMLElement {
        let subtitleContainer = document.getElementById("mkplayer-subtitle-container");
        if (!subtitleContainer) {
            subtitleContainer = document.createElement("div");
            subtitleContainer.setAttribute("id", "mkplayer-subtitle-container");
        } else {
            subtitleContainer.textContent = "";
        }
        subtitleContainer.style.position = "absolute";
        subtitleContainer.style.zIndex = "2";
        subtitleContainer.style.top = "0";
        subtitleContainer.style.left = "0";
        subtitleContainer.style.border = "0";
        subtitleContainer.style.margin = "0";
        subtitleContainer.style.padding = "0";
        subtitleContainer.style.width = "100%";
        subtitleContainer.style.height = "100%";
        subtitleContainer.style.pointerEvents = 'none';
        return subtitleContainer;
    }

    private getSubtitleRegion(): HTMLElement {
        let subtitleRegion = document.getElementById("mkplayer-subtitle-region");
        if (!subtitleRegion) {
            subtitleRegion = document.createElement("div");
            subtitleRegion.setAttribute("id", "mkplayer-subtitle-region");
        } else {
            subtitleRegion.textContent = "";
        }
        subtitleRegion.style.cssText = MKSubtitleOverlay.DEFAULT_REGION_STYLE;
        return subtitleRegion;
    }

    private rgbaCheck(str: string): string {
        let matches = str.match(/rgba\((.*?)\)/g);
        if (matches !== null) {
            matches.forEach((match: any) => {
                let r = match.split(',');
                r[3] = `${r[3].replace(')', "") / 255})`;
                str = str.replace(match, r.join(','));
            });
        } else if (!str.match(/img/g)) {
            str = `<div style="font-size:0.80c;text-align:center;color:rgba(255,255,255,1);background-color:rgba(0,0,0,0.7529411764705882);font-family:sans-serif;">${str}</div>`;
        }
        return str;
    }

    private styleTextToObject(str: string): any {
        let slplitStr = str.replace(/ /g, "").replace(/%/g, "").replace(/;/g, " ").trim().split(" ");
        let result: {[key: string]: string} = {};
        slplitStr.forEach((item: string) => {
            let splitItem = item.split(":");
            if (splitItem[0] && splitItem[1]) {
                result[splitItem[0]] = splitItem[1];
            }
        });
        return result;
    }

    private updateRegion(cue?: MKSubtitleCueEvent) {
        const cssText = cue?.regionStyle ?? MKSubtitleOverlay.DEFAULT_REGION_STYLE;
        let cellResolution = MKSubtitleOverlay.DEFAULT_CELL_RESOLUTION;
        let tmp = cssText.match(/cellResolutionRows:([\d]+);/);
        if (tmp && tmp.length >= 2) {
            if (tmp[1]) {
                let rows = parseInt(tmp[1])
                cellResolution.rows = rows;
            }
        }
        tmp = cssText.match(/cellResolutionColumns:([\d]+);/);
        if (tmp && tmp.length >= 2) {
            if (tmp[1]) {
                let columns = parseInt(tmp[1]);
                cellResolution.columns = columns;
            }
        }

        // update subtitle container
        this.subtitleContainer.style.width = this.videoContainer.offsetWidth + "px";
        this.subtitleContainer.style.height = this.videoContainer.offsetHeight + "px";

        // update subtitle region
        const styleOb = this.styleTextToObject(cssText);
        this.subtitleRegion.style.cssText = cssText;
        this.subtitleRegion.style.position = 'absolute';
        this.subtitleRegion.style.top = parseFloat(styleOb.top) / 100 * this.videoContainer.offsetHeight + "px";
        this.subtitleRegion.style.left = parseFloat(styleOb.left) / 100 * this.videoContainer.offsetWidth + "px";
        this.subtitleRegion.style.width = parseFloat(styleOb.width) / 100 * this.videoContainer.offsetWidth + "px";
        this.subtitleRegion.style.height = parseFloat(styleOb.height) / 100 * this.videoContainer.offsetHeight + "px";
        const fontSize = (0.6 * Math.round(this.videoContainer.offsetHeight / cellResolution.rows)) + "px";
        this.subtitleRegion.style.lineHeight = fontSize;
        this.subtitleRegion.style.fontSize = fontSize;
        // observer is added as the img element is added dynamically and we need to set the width and height of the img element to 100% of the parent element
        const observer = new MutationObserver(() => {
            const imageSubtitle = Array.from(this.subtitleRegion.querySelectorAll("img"));
            imageSubtitle.forEach((img) => {
                img.style.maxHeight = '100%';
                img.style.maxWidth = '100%';
            });
        });
        observer.observe(this.subtitleRegion, { childList: true });
    }

    private applySubtitleStyle(node?: HTMLElement) {
        if (!this.subtitleStyle) return;

        const style = this.subtitleStyle;
        const nodes: HTMLElement[] = node ? [node] : this.subtitleRegion ? Array.from(this.subtitleRegion.children) as HTMLElement[] : [];

        nodes.forEach(n => {
            if (style.fontSize) { n.style.fontSize = style.fontSize.toString() }
            if (style.fontStyle) { n.style.fontStyle = style.fontStyle }
            if (style.fontStyle === "bold") {n.style.fontWeight = "bold"}
            if (style.fontFamily) { n.style.fontFamily = style.fontFamily === "normal" ? "" : style.fontFamily; }
            if (style.fontColor) { n.style.color = style.fontColor }
            if (style.fontOpacity !== undefined) { n.style.opacity = style.fontOpacity.toString() }
            if (style.backgroundColor) { n.style.backgroundColor = style.backgroundColor }
        });
    }

    enable() {
        this.reset();
        this.enabled = true;
    }

    disable() {
        this.hide();
        this.enabled = false;
    }

    show(cue: MKSubtitleCueEvent) {
        if (!this.enabled) return;
        this.updateRegion(cue);
        if (cue.html) {
            const data = this.rgbaCheck(cue.html);
            const element = new DOMParser().parseFromString(data, "text/html").body.firstChild as HTMLElement;
            this.applySubtitleStyle(element);
            this.subtitleRegion.appendChild(element);
        } else {
            const position = cue.position ?? {row: 11, column: 8};
            const element = document.createElement('div');
            element.textContent = cue.text;
            element.id = `${position.row}_${position.column}_${cue.text}`;
            element.style.position = 'absolute';
            element.style.backgroundColor = "black";
            element.style.color = "white";
            element.style.fontFamily = 'Menlo, Consolas, "Cutive Mono", monospace';
            const fontSize = (0.6 * this.subtitleRegion.offsetHeight) / MKSubtitleOverlay.CEA608_NUM_ROWS + "px";
            element.style.fontSize = element.style.lineHeight = fontSize;
            element.style.margin = (0.1 * this.subtitleRegion.offsetHeight) / MKSubtitleOverlay.CEA608_NUM_ROWS + "px";
            element.style.letterSpacing = 0.215 * (0.6 * this.subtitleRegion.offsetWidth) / MKSubtitleOverlay.CEA608_NUM_COLUMNS + "px";
            element.style.top = this.subtitleRegion.offsetTop + (0.1 * this.subtitleRegion.offsetHeight) + position.row * (0.6 * this.subtitleRegion.offsetHeight) / MKSubtitleOverlay.CEA608_NUM_ROWS + "px";
            element.style.left = this.subtitleRegion.offsetLeft + (0.1 * this.subtitleRegion.offsetWidth) + position.column * (0.6 * this.subtitleRegion.offsetWidth) / MKSubtitleOverlay.CEA608_NUM_COLUMNS + "px";
            this.applySubtitleStyle(element);
            this.subtitleRegion.appendChild(element);
        }
    }

    setSubtitleUI(options: {
        fontSize?: "50";
        fontStyle?: "normal";
        fontFamily?: "normal";
        fontColor?: "white";
        fontOpacity?: "75";
        backgroundColor?: "black";
    }) {
        const fontSize = Number(options.fontSize);
        const fontOpacity = Number(options.fontOpacity);
        const allowedFontStyle = ["normal", "italic", "bold"];
        const allowedFontFamily = ["Times New Roman","fantasy","cursive","serif","monospace","normal"];
        const allowedColor = ["normal", "white", "black", "red", "green", "blue", "cyan", "yellow", "magenta"];

        if (fontSize > 0 && fontSize <= 100) {
            this.subtitleStyle.fontSize =`${fontSize}%` ;
        }
        if (options.fontStyle && allowedFontStyle.includes(options.fontStyle)) {
            this.subtitleStyle.fontStyle = options.fontStyle;
        }
        if (options.fontFamily && allowedFontFamily.includes(options.fontFamily)) {
            this.subtitleStyle.fontFamily = options.fontFamily;
        }
        if (options.fontColor && allowedColor.includes(options.fontColor)) {
            this.subtitleStyle.fontColor = options.fontColor;
        }
        if (fontOpacity >= 0 && fontOpacity <= 100) {
            this.subtitleStyle.fontOpacity = fontOpacity / 100;
        }
        if (options.backgroundColor && allowedColor.includes(options.backgroundColor)) {
            this.subtitleStyle.backgroundColor = options.backgroundColor;
        }

        this.applySubtitleStyle();
    }

    hide() {
        if (!this.enabled) return;
        const children = this.subtitleRegion.childNodes;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child) {
                this.subtitleRegion.removeChild(child);
            }
        }
        this.subtitleRegion.textContent = "";
    }

    updateFontSize() {
        if (!this.enabled) return;
        this.updateRegion();
    }

    destroy() {
        if (this.videoContainer && this.subtitleContainer && this.subtitleRegion) {
            this.subtitleContainer.removeChild(this.subtitleRegion);
            this.videoContainer.removeChild(this.subtitleContainer);
            this.enabled = false;
        }
    }
}